import type { EnhancedSectionContent } from '@/components/GenericComponent/GenericComponent';
import type {
  ExitIntentCF,
  FeatureCardCF,
  HeaderMenuItemCF,
  LinkCF,
  SectionConstraintsCF,
  SectionStateConstraintCF,
} from '@/types';
import { isNotUndefined } from '@/types/utility-types';
import { checkStateConstraint } from '@/utils/constraint';
import useEcommMarket from './geo';

export interface GetConstrainedContentParameters {
  constrainedSection: SectionConstraintsCF;
  include?: string[];
}

export type ConstrainedContent =
  | EnhancedSectionContent
  | ExitIntentCF
  | FeatureCardCF
  | HeaderMenuItemCF
  | LinkCF
  | undefined;

export type GetConstrainedContent = <T extends ConstrainedContent>(
  params: GetConstrainedContentParameters
) => T[] | undefined;

export const useGetConstrainedContent = () => {
  const { userLocation } = useEcommMarket();

  // Checks which state constraint applies to the user's location and returns the default or alternate content
  const getConstrainedContent = <T extends ConstrainedContent>({
    constrainedSection,
    include,
  }: GetConstrainedContentParameters): T[] | undefined => {
    const { default: defaultContent, constraints = [] } =
      constrainedSection?.fields || {};

    const definedConstraints = constraints.filter(isNotUndefined);

    // If the user's location is known, check for matching constraints and set the alternate content
    let alternateContent: ConstrainedContent[] | undefined;
    if (userLocation && (userLocation.state || userLocation.zipCode)) {
      const matchingConstraint = definedConstraints.find(
        (stateConstraint: SectionStateConstraintCF) => {
          return checkStateConstraint({ stateConstraint, userLocation });
        }
      );
      alternateContent = matchingConstraint?.fields.alternateContent as
        | ConstrainedContent[]
        | undefined;
    }

    const matchingContentArray = alternateContent || defaultContent;

    // If include is passed, filter out the content that isn't the correct type
    const filteredMatchingContentArray =
      include && matchingContentArray
        ? matchingContentArray.filter((content) =>
            include.includes(content?.sys.contentType.sys.id || '')
          )
        : matchingContentArray;

    return filteredMatchingContentArray?.length
      ? (filteredMatchingContentArray as T[])
      : undefined;
  };

  return getConstrainedContent;
};
